import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { products } from '../mock/mockData';
import {
  calcMonthlyPayment, formatAzn, formatAmountInt,
  DEFAULT_INTEREST_RATE,
  AMOUNT_MIN, AMOUNT_MAX, AMOUNT_STEP,
  TERM_MIN, TERM_MAX, TERM_STEP,
  HERO_EXAMPLE_AMOUNT, HERO_EXAMPLE_TERM
} from '../utils/loanCalc';
import { useApplicationModal } from '../context/ApplicationModalContext';
import { SEO, buildOrganizationLd } from '../components/SEO';
import {
  Wallet, Briefcase, Car, Zap, Eye, Users, ArrowRight, MessageCircle,
  Gem, Banknote, PhoneCall, FileCheck, CheckCircle2, Shield, TrendingUp
} from 'lucide-react';

const iconMap = {
  Wallet, Briefcase, Car, Zap, Eye, Users, Gem, Banknote, PhoneCall, FileCheck
};

export const HomePage = () => {
  const { openModal } = useApplicationModal();
  const [heroAmount, setHeroAmount] = useState(HERO_EXAMPLE_AMOUNT);
  const [heroTerm, setHeroTerm] = useState(HERO_EXAMPLE_TERM);

  const heroMonthly = useMemo(
    () => calcMonthlyPayment(heroAmount, heroTerm, DEFAULT_INTEREST_RATE),
    [heroAmount, heroTerm]
  );

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="İDK BOKT | Kredit məhsulları və onlayn müraciət"
        description="İnkişafa Dəstək Kreditləri — Azərbaycanda etibarlı BOKT. İstehlak krediti, qızıl krediti və avto lizinq. Onlayn müraciət və şəffaf şərtlər."
        path="/"
        jsonLd={buildOrganizationLd()}
      />
      {/* Hero Section - clean & elegant, split layout */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-300 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left: Text + CTAs */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <span className="inline-block text-xs font-semibold text-blue-200 uppercase tracking-[0.25em] mb-4">
                2011-ci ildən etibarən
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-[1.1] font-serif-display">
                Planlarınız üçün<br />
                <span className="text-blue-200">peşəkar maliyyə dəstəyi</span>
              </h1>
              <p className="text-base sm:text-lg text-blue-100 max-w-md mx-auto lg:mx-0 mb-6 leading-relaxed">
                Şəffaf şərtlər və ehtiyacınıza uyğun kredit həlləri.
              </p>
              <div className="flex justify-center lg:justify-start">
                <Button
                  size="lg"
                  className="bg-white text-blue-900 hover:bg-blue-50 px-8 py-6 text-base w-full sm:w-auto min-w-[260px]"
                  data-testid="hero-order-btn"
                  onClick={() => openModal()}
                >
                  Kredit üçün müraciət et
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Right: Interactive credit calculator */}
            <div className="lg:col-span-5" data-testid="hero-calc">
              <div className="rounded-2xl border border-white/20 bg-white/[0.08] backdrop-blur-sm px-6 py-6 sm:px-7 sm:py-7">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-100 mb-5">
                  Kredit kalkulyatoru
                </p>

                {/* Amount */}
                <div className="mb-6">
                  <div className="flex items-baseline justify-between mb-3">
                    <span className="text-sm text-blue-100">Məbləğ</span>
                    <span
                      className="text-xl sm:text-2xl font-bold text-white tabular-nums"
                      data-testid="hero-calc-amount"
                    >
                      {formatAmountInt(heroAmount)}{' '}
                      <span className="text-sm font-medium text-blue-100">AZN</span>
                    </span>
                  </div>
                  <Slider
                    variant="dark"
                    value={[heroAmount]}
                    onValueChange={([v]) => setHeroAmount(v)}
                    min={AMOUNT_MIN}
                    max={AMOUNT_MAX}
                    step={AMOUNT_STEP}
                    data-testid="hero-calc-amount-slider"
                    aria-label="Kredit məbləği"
                  />
                  <div className="flex justify-between text-[11px] text-blue-100/70 mt-2 tabular-nums">
                    <span>{formatAmountInt(AMOUNT_MIN)}</span>
                    <span>{formatAmountInt(AMOUNT_MAX)}</span>
                  </div>
                </div>

                {/* Term */}
                <div className="mb-5 pb-5 border-b border-white/15">
                  <div className="flex items-baseline justify-between mb-3">
                    <span className="text-sm text-blue-100">Müddət</span>
                    <span
                      className="text-xl sm:text-2xl font-bold text-white tabular-nums"
                      data-testid="hero-calc-term"
                    >
                      {heroTerm} <span className="text-sm font-medium text-blue-100">ay</span>
                    </span>
                  </div>
                  <Slider
                    variant="dark"
                    value={[heroTerm]}
                    onValueChange={([v]) => setHeroTerm(v)}
                    min={TERM_MIN}
                    max={TERM_MAX}
                    step={TERM_STEP}
                    data-testid="hero-calc-term-slider"
                    aria-label="Kredit müddəti"
                  />
                  <div className="flex justify-between text-[11px] text-blue-100/70 mt-2 tabular-nums">
                    <span>{TERM_MIN} ay</span>
                    <span>{TERM_MAX} ay</span>
                  </div>
                </div>

                {/* Monthly result */}
                <div className="mb-5">
                  <p className="text-xs text-blue-100/90 mb-1">Təxmini aylıq ödəniş</p>
                  <p
                    className="text-4xl sm:text-5xl font-bold text-white tabular-nums leading-none"
                    data-testid="hero-monthly-value"
                  >
                    {formatAzn(heroMonthly)}{' '}
                    <span className="text-xl sm:text-2xl text-blue-100 font-medium">AZN</span>
                  </p>
                </div>

                <p className="mt-3 text-[11px] text-blue-100/80 text-center leading-relaxed">
                  Hesablama məlumat xarakterlidir və rəsmi kredit təklifi deyil.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-[0.2em]">Məhsullarımız</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-3 leading-tight font-serif-display">
              Sizin üçün seçdiyimiz kredit həlləri
            </h2>
            <p className="text-base text-gray-600 leading-relaxed">
              Fərqli ehtiyaclara uyğun üç fərqli məhsul — hər biri şəffaf şərtlər və peşəkar dəstəklə.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {products.map((product) => {
              const rateTerm = product.details?.terms?.find(t => t.label.includes('Faiz'));
              const amountTerm = product.details?.terms?.find(t => t.label.includes('məbləği'));
              return (
                <Link
                  key={product.id}
                  to={`/mehsullar/${product.slug}`}
                  className="group relative flex flex-col p-6 sm:p-7 bg-white border border-gray-200 rounded-2xl hover:border-blue-300 hover:shadow-lg transition-all duration-300"
                  data-testid={`product-card-${product.slug}`}
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{product.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">{product.description}</p>

                  <div className="grid grid-cols-2 gap-4 py-5 border-t border-gray-100 mb-6">
                    {amountTerm && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Məbləğ</p>
                        <p className="text-sm font-semibold text-gray-900">{amountTerm.value}</p>
                      </div>
                    )}
                    {rateTerm && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Faiz</p>
                        <p className="text-sm font-semibold text-gray-900">{rateTerm.value}</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-auto flex items-center text-sm font-medium text-blue-700 group-hover:gap-2 gap-1 transition-all">
                    <span>{product.title} haqqında ətraflı</span>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Help / Contact invitation section */}
      <section className="py-14 lg:py-16 bg-gray-50 border-t border-gray-200/70" data-testid="help-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight font-serif-display">
            Sualınız var? Bizə yazın.
          </h2>
          <p className="text-base text-gray-600 mb-8 leading-relaxed max-w-xl mx-auto">
            Suallarınız üçün əməkdaşlarımız hər zaman əlçatandır.
          </p>
          <Link to="/elaqe">
            <Button
              size="lg"
              className="bg-blue-700 hover:bg-blue-800 text-white px-8"
              data-testid="help-contact-btn"
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              Bizimlə əlaqə saxla
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQ moved to /faq page */}
    </div>
  );
};
